import React from 'react'
import { useTranslation } from 'react-i18next'
import { FieldHookConfig, useField } from 'formik'
import { TextField as UITextField } from '@carsys/ui'
import { isInvalid } from './utils'
import { useFormSection } from './FormSection'
import { useRegisterFormField } from './TabbedForm/FormTabPanel'

interface TextFieldProps<Value>
  extends Omit<React.ComponentProps<typeof UITextField>, 'component' | 'defaultChecked' | 'defaultValue' | 'name' | 'value'>,
    Pick<FieldHookConfig<Value>, 'name' | 'validate'> {
  transform?: (value: Value) => Value
}

const identity = (value: any) => value

export function TextField<Value>({ name, validate, transform = identity, onChange, ...props }: TextFieldProps<Value>) {
  const { t, i18n } = useTranslation()
  const [input, meta] = useField<Value>({ name, validate })
  const formSection = useFormSection()
  useRegisterFormField(name)

  const handleOnChange = (event: React.ChangeEvent<any>) => {
    const { selectionStart, selectionEnd } = event.target

    if (event.target.setSelectionRange && (props.type === undefined || props.type === 'text')) {
      window.requestAnimationFrame(() => {
        event.target.setSelectionRange(selectionStart, selectionEnd)
      })
    }

    event.target.value = transform(event.target.value)
    input.onChange(event)
    if (typeof onChange === 'function') {
      onChange(event)
    }
  }

  const metaError = i18n.exists(meta.error!) ? t(meta.error!) : meta.error
  const propsHelperText = i18n.exists(String(props.helperText!)) ? t(String(props.helperText!)) : props.helperText
  return (
    <UITextField
      id={input.name}
      error={isInvalid(meta)}
      disabled={formSection.disabled}
      {...props}
      {...input}
      onChange={handleOnChange}
      helperText={isInvalid(meta) ? metaError : propsHelperText}
    />
  )
}
